import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useChat } from '../hooks/useChat';
import { useChatHistory } from '../hooks/useChatHistory';
import MessageBubble from './MessageBubble';
import ChatInput from './ChatInput';
import WelcomeScreen from './WelcomeScreen';
import StreamingIndicator from './StreamingIndicator';
import { Bot, Sparkles, HelpCircle, X } from 'lucide-react';

const ChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
`;

const Header = styled.div`
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 24px 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  direction: rtl;
`;

const HeaderTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #2d3748;
  display: flex;
  align-items: center;
  gap: 12px;
  direction: rtl;

  svg {
    width: 32px;
    height: 32px;
    color: #1B8C3C;
  }
`;

const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const MessagesContainer = styled.div`
  overflow-y: auto;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  flex: 1;
`;

const MessagesEnd = styled.div`
  height: 1px;
`;

const ChatContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const MessagesSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const InputSection = styled.div`
  flex-shrink: 0;
  border-top: 1px solid #e2e8f0;
`;

// Welcome screen specific layout - no resize, clean flow
const WelcomeWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const WelcomeContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0;
`;

const WelcomeInputSection = styled.div`
  flex-shrink: 0;
  border-top: 1px solid #e2e8f0;
  background: white;
`;

const HelpIconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  margin-left: 12px;

  &:hover {
    background: #f7fafc;
  }

  svg {
    width: 24px;
    height: 24px;
    color: #718096;
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e2e8f0;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #2d3748;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: #1B8C3C;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #f7fafc;
  }

  svg {
    width: 24px;
    height: 24px;
    color: #718096;
  }
`;

const ModalSection = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionText = styled.p`
  font-size: 15px;
  color: #4a5568;
  line-height: 1.6;
  margin-bottom: 12px;
`;

const ExampleCard = styled.div`
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin-top: 12px;
`;

const ExampleLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1B8C3C;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ExampleText = styled.div`
  font-size: 14px;
  color: #2d3748;
  font-family: monospace;
`;


function ChatArea({ setActiveTab }) {
  const {
    messages,
    isProcessing,
    processingStep,
    currentSteps,
    connected,
    messagesEndRef,
    actions
  } = useChat();

  const { currentSessionId } = useChatHistory();

  const hasMessages = messages.length > 0;

  // Help modal state
  const [showHelpModal, setShowHelpModal] = useState(false);

  return (
    <ChatContainer>
      <Header>
        <HeaderTitle>
          <Bot />
          مساعد قاعدة البيانات الذكي
          <HelpIconButton onClick={() => setShowHelpModal(true)} title="مساعدة">
            <HelpCircle />
          </HelpIconButton>
        </HeaderTitle>
        <StatusBadge style={{
          background: connected
            ? 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)'
            : 'linear-gradient(135deg, #ed8936 0%, #dd6b20 100%)'
        }}>
          <Sparkles />
          {connected ? 'متصل' : 'جاري الاتصال...'}
        </StatusBadge>
      </Header>

      <ChatContent>
        {!hasMessages ? (
          /* Welcome screen: clean layout without resize handle */
          <WelcomeWrapper>
            <WelcomeContent>
              <WelcomeScreen setActiveTab={setActiveTab} />
            </WelcomeContent>
            <WelcomeInputSection>
              <ChatInput />
            </WelcomeInputSection>
          </WelcomeWrapper>
        ) : (
          /* Chat messages layout */
          <>
            <MessagesSection>
              <MessagesContainer>
                {messages.map((message, index) => {
                  // For bot messages, find the preceding user question
                  let userQuestion = null;
                  if (!message.isUser) {
                    // First try reformulation (if question was reformulated)
                    if (message.reformulation?.original_question) {
                      userQuestion = message.reformulation.original_question;
                    } else {
                      // Otherwise, look for the previous user message
                      for (let i = index - 1; i >= 0; i--) {
                        if (messages[i].isUser) {
                          userQuestion = messages[i].content;
                          break;
                        }
                      }
                    }
                  }

                  return (
                    <MessageBubble
                      key={message.id}
                      message={message}
                      sessionId={currentSessionId}
                      onSendMessage={actions.sendMessage}
                      onApproveGoal={actions.runApprovedGoal}
                      userQuestion={userQuestion}
                    />
                  );
                })}

                <StreamingIndicator
                  currentSteps={currentSteps}
                  processingStep={processingStep}
                  isProcessing={isProcessing}
                />
                <MessagesEnd ref={messagesEndRef} />
              </MessagesContainer>
            </MessagesSection>

            <InputSection>
              <ChatInput />
            </InputSection>
          </>
        )}
      </ChatContent>

      {/* Help Modal */}
      <AnimatePresence>
        {showHelpModal && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowHelpModal(false)}
          >
            <ModalContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>
                  <HelpCircle />
                  الاقتراحات السياقية الذكية
                </ModalTitle>
                <CloseButton onClick={() => setShowHelpModal(false)}>
                  <X />
                </CloseButton>
              </ModalHeader>

              <ModalSection>
                <SectionTitle>💡 ما هي الاقتراحات السياقية؟</SectionTitle>
                <SectionText>
                  عندما تطرح سؤالاً له إجابات متعددة محتملة، يكتشف المساعد الذكي هذا الغموض تلقائياً ويعرض لك:
                </SectionText>
                <SectionText>
                  • <strong>تفسير</strong>: لماذا ترى البدائل<br />
                  • <strong>خيارات بديلة</strong>: سيناريوهات، وحدات، أو سنوات أخرى متاحة<br />
                  • <strong>إجراءات سريعة</strong>: أزرار قابلة للنقر لتحسين استعلامك
                </SectionText>
              </ModalSection>

              <ModalSection>
                <SectionTitle>🎯 متى تظهر؟</SectionTitle>
                <SectionText>
                  تظهر الاقتراحات السياقية تلقائياً عندما يرجع استعلامك بيانات تحتوي على:
                </SectionText>
                <SectionText>
                  • <strong>سيناريوهات متعددة</strong> (فعلي، ميزانية، متوقع، مقدر)<br />
                  • <strong>وحدات متعددة</strong> (ريال سعودي، نسبة مئوية، ملايين)<br />
                  • <strong>سنوات متعددة</strong> (فترات زمنية مختلفة)
                </SectionText>
                <ExampleCard>
                  <ExampleLabel>مثال على الاستعلام</ExampleLabel>
                  <ExampleText>"ما هي الإيرادات في ٢٠٢٥؟"</ExampleText>
                </ExampleCard>
                <SectionText style={{ marginTop: '12px' }}>
                  قد يرجع هذا الاستعلام ٨ صفوف (٤ سيناريوهات × ٢ وحدة)، لذا يعرض لك المساعد الذكي البدائل لمساعدتك في اختيار ما تحتاجه بالضبط.
                </SectionText>
              </ModalSection>

              <ModalSection>
                <SectionTitle>🔄 محادثات متعددة الأدوار</SectionTitle>
                <SectionText>
                  يتذكر المساعد السياق من استعلامك السابق، مما يجعل الأسئلة اللاحقة أكثر ذكاءً:
                </SectionText>
                <ExampleCard>
                  <ExampleLabel>الاستعلام الأول</ExampleLabel>
                  <ExampleText>"أظهر الإيرادات لعام ٢٠٢٥"</ExampleText>
                  <div style={{ marginTop: '8px', fontSize: '13px', color: '#718096' }}>
                    ← يرجع: الإيرادات - الفعلي، ٢٠٢٥
                  </div>
                </ExampleCard>
                <ExampleCard>
                  <ExampleLabel>الاستعلام التالي</ExampleLabel>
                  <ExampleText>"كنسبة مئوية"</ExampleText>
                  <div style={{ marginTop: '8px', fontSize: '13px', color: '#718096' }}>
                    ← يتحول تلقائياً إلى: "أظهر الإيرادات - الفعلي لعام ٢٠٢٥ كنسبة مئوية"
                  </div>
                </ExampleCard>
              </ModalSection>

              <ModalSection>
                <SectionTitle>✨ القيم الافتراضية الذكية</SectionTitle>
                <SectionText>
                  عندما تطرح سؤالاً عاماً، يستخدم النظام قيم افتراضية ذكية لعرض النتائج الأكثر صلة أولاً:
                </SectionText>
                <SectionText>
                  • <strong>أولوية السيناريو</strong>: الفعلي ← المقدر ← المتوقع ← الميزانية<br />
                  • <strong>أولوية الوحدة</strong>: ريال سعودي ← النسب المئوية<br />
                  • <strong>أولوية السنة</strong>: الأحدث أولاً
                </SectionText>
              </ModalSection>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </ChatContainer>
  );
}

export default ChatArea; 